import {assignInlineVars} from '@vanilla-extract/dynamic';
import {NextPage} from 'next';
import {useRouter} from 'next/router';

import {Screen} from '#component/base';
import {ImageCTACard, InternalNavBar} from '#component/partial';
import {navColorVar} from '#component/partial/InternalNavBar/InternalNavBar.css';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {withRequireUserState} from '#component/util/RequireUserState/RequireUserState';
import {colorValues} from '#theme/default/colors/colors';
import {hexToRgba} from '#util/hexToRgba';

import {HealthFactorRecommendations} from './component/HealthFactorRecommendations';
import {HealthFactorReferences} from './component/HealthFactorReferences';
import {HealthFactorScience} from './component/HealthFactorScience';
import {HealthFactorSummary} from './component/HealthFactorSummary';
import {anchorNames, anchors} from './HealthFactorAssessment.util';
import {useHealthFactorAssessment} from './useHealthFactorAssessment';

import * as css from './HealthFactorAssessment.css';

export const HealthFactorAssessmentScreen = () => {
  const {
    answer,
    color,
    hasActionPlan,
    healthFactor,
    healthFactorAssessment,
    loading,
    question,
    researchSnack,
  } = useHealthFactorAssessment();
  const hf = healthFactorAssessment?.healthFactor || healthFactor;
  const colorValue = colorValues[color];
  const {track} = useAnalytics();
  const {query} = useRouter();
  const {healthFactorId} = query;

  const handleCTAClick = () => {
    track<UserActionEvent>({
      evtName: EventType.UserAction,
      actionName: 'View GAP reco cta',
    });
  };

  return (
    <Screen
      hideHeader
      loading={loading}
      pageName="Survey Summary Results"
      pageData={{healthFactorId}}
      style={assignInlineVars({
        [css.healthFactorColor]: colorValue,
        [css.healthFactorFadeEndColor]: hexToRgba(colorValue, 0),
        [css.healthFactorFadeStartColor]: hexToRgba(colorValue, 0.25),
        [navColorVar]: colorValue,
      })}
      withDefaultSizing={false}>
      <InternalNavBar links={anchors} />

      <HealthFactorSummary
        meetsRequirement={healthFactorAssessment?.meetsRequirements ?? true}
        impactLevel={hf?.impactLevel || undefined}
        question={question?.text || ''}
        answer={answer || ''}
        recommendedAnswer={hf?.recommendedResponse || ''}
        title={hf?.title || ''}
        imageUrl={hf?.imageUrl || undefined}
        id={anchorNames.summary}
        emergingResearch={!!hf?.emergingResearch}
      />

      <div className={css.indicator}>
        <div className={css.indicatorBar} />
      </div>

      <HealthFactorRecommendations
        title={hf?.recommendationTitle || ''}
        description={hf?.recommendationDescription || ''}
      />

      <HealthFactorScience
        title={researchSnack?.title || ''}
        description={researchSnack?.description || ''}
      />

      <HealthFactorReferences references={hf?.references || []} />

      {!hasActionPlan && (
        <div className={css.primaryGrid}>
          <ImageCTACard
            onClick={handleCTAClick}
            headline="Get your Action Plan"
            imageUrl="/image/action-plan.jpg"
            href="/actionplan"
            lede="Receive a personalized, step-by-step checklist to improve your lifestyle and see projections into the future."
          />
        </div>
      )}
    </Screen>
  );
};

export const HealthFactorAssessment: NextPage = withRequireUserState(HealthFactorAssessmentScreen, {
  userState: 'verified',
});
